import React from 'react';
import type { CanvasSectionBlock } from '../sectionRegistry';
import { isSectionBackdropModelViewerBlock } from '../blockRenderers/modelViewerSceneBackdrop';
import { TOTAL_COLUMNS } from './blockFrame';
import { type AlignmentBox, createAlignmentBoxFromLayout } from './alignmentMath';

export type EditorBoundsMode = 'frame' | 'content' | 'text-paragraph';

export type EditorBoundsBox = {
  blockId: string;
  mode: EditorBoundsMode;
  layerWidth: number;
  layerHeight: number;
  frameBox: AlignmentBox;
  contentBox: AlignmentBox | null;
  /** Visual bounds for selection, alignment, gap guides, and snapping. */
  activeVisualBox: AlignmentBox;
  /** Layout/editing frame for wrapping, resize handles, and text editing. */
  editingFrameBox: AlignmentBox;
  /** Alias for `activeVisualBox` (movement/alignment consumers). */
  activeBox: AlignmentBox;
};

type UseEditorBoundsRegistryOptions = {
  enabled: boolean;
  layerRef: React.RefObject<HTMLDivElement | null>;
  blocks: CanvasSectionBlock[];
  colWidth: number;
  rowHeight: number;
};

const CONTENT_SELECTOR = '[data-builder-editor-bounds="content"]';
const BLOCK_ROOT_SELECTOR = '[data-builder-block-root="true"]';
const TEXT_CONTENT_SELECTOR = '[data-builder-text-content="true"]';
const TOLERANCE_PX = 0.5;

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function finiteNumber(value: unknown, fallback: number) {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function makeBox(id: string, left: number, top: number, width: number, height: number): AlignmentBox {
  const resolvedWidth = Math.max(1, width);
  const resolvedHeight = Math.max(1, height);
  const right = left + resolvedWidth;
  const bottom = top + resolvedHeight;
  return {
    id,
    left,
    top,
    width: resolvedWidth,
    height: resolvedHeight,
    right,
    bottom,
    centerX: left + resolvedWidth / 2,
    centerY: top + resolvedHeight / 2,
  };
}

function resolveLayerLayoutMetrics(layer: HTMLElement) {
  const layerRect = layer.getBoundingClientRect();
  const layoutWidth = layer.offsetWidth > 0 ? layer.offsetWidth : layerRect.width;
  const layoutHeight = layer.offsetHeight > 0 ? layer.offsetHeight : layerRect.height;
  const scaleX = layerRect.width > 0 ? layoutWidth / layerRect.width : 1;
  const scaleY = layerRect.height > 0 ? layoutHeight / layerRect.height : 1;
  return { layerRect, layoutWidth, layoutHeight, scaleX, scaleY };
}

function elementBoxRelativeToLayer(
  id: string,
  element: HTMLElement,
  layer: HTMLElement
): AlignmentBox {
  const { layerRect, scaleX, scaleY } = resolveLayerLayoutMetrics(layer);
  const rect = element.getBoundingClientRect();
  return makeBox(
    id,
    (rect.left - layerRect.left) * scaleX,
    (rect.top - layerRect.top) * scaleY,
    rect.width * scaleX,
    rect.height * scaleY
  );
}

function unionRectsRelativeToLayer(
  id: string,
  rects: DOMRect[],
  layer: HTMLElement
): AlignmentBox | null {
  const { layerRect, scaleX, scaleY } = resolveLayerLayoutMetrics(layer);
  const visibleRects = rects.filter((rect) =>
    [rect.left, rect.top, rect.right, rect.bottom, rect.width, rect.height].every(Number.isFinite) &&
    rect.width > 0 &&
    rect.height > 0
  );
  if (visibleRects.length === 0) return null;

  const left = (Math.min(...visibleRects.map((rect) => rect.left)) - layerRect.left) * scaleX;
  const top = (Math.min(...visibleRects.map((rect) => rect.top)) - layerRect.top) * scaleY;
  const right = (Math.max(...visibleRects.map((rect) => rect.right)) - layerRect.left) * scaleX;
  const bottom = (Math.max(...visibleRects.map((rect) => rect.bottom)) - layerRect.top) * scaleY;
  return makeBox(id, left, top, right - left, bottom - top);
}

function textContentBoxRelativeToLayer(
  id: string,
  element: HTMLElement,
  layer: HTMLElement
): AlignmentBox | null {
  if (typeof document === 'undefined') return null;
  const walker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        return node.textContent?.trim()
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      },
    }
  );
  const rects: DOMRect[] = [];
  let node = walker.nextNode();
  while (node) {
    const range = document.createRange();
    range.selectNodeContents(node);
    rects.push(...Array.from(range.getClientRects()));
    range.detach();
    node = walker.nextNode();
  }
  return unionRectsRelativeToLayer(id, rects, layer);
}

function imageContentBoxRelativeToLayer(
  id: string,
  element: HTMLElement,
  layer: HTMLElement
): AlignmentBox {
  const { layerRect, scaleX, scaleY } = resolveLayerLayoutMetrics(layer);
  const elementRect = element.getBoundingClientRect();
  const baseLeft = (elementRect.left - layerRect.left) * scaleX;
  const baseTop = (elementRect.top - layerRect.top) * scaleY;
  const objectFit = window.getComputedStyle(element).objectFit;

  if (
    element instanceof HTMLImageElement &&
    objectFit === 'contain' &&
    element.naturalWidth > 0 &&
    element.naturalHeight > 0 &&
    elementRect.width > 0 &&
    elementRect.height > 0
  ) {
    const layoutWidth = elementRect.width * scaleX;
    const layoutHeight = elementRect.height * scaleY;
    const assetRatio = element.naturalWidth / element.naturalHeight;
    const frameRatio = layoutWidth / layoutHeight;
    const width = assetRatio > frameRatio ? layoutWidth : layoutHeight * assetRatio;
    const height = assetRatio > frameRatio ? layoutWidth / assetRatio : layoutHeight;
    return makeBox(
      id,
      baseLeft + (layoutWidth - width) / 2,
      baseTop + (layoutHeight - height) / 2,
      width,
      height
    );
  }

  return makeBox(id, baseLeft, baseTop, elementRect.width * scaleX, elementRect.height * scaleY);
}

function contentBoxRelativeToLayer(
  block: CanvasSectionBlock,
  element: HTMLElement,
  layer: HTMLElement
): AlignmentBox {
  if (block.type === 'text' && element.matches(TEXT_CONTENT_SELECTOR)) {
    return textContentBoxRelativeToLayer(block.id, element, layer)
      ?? elementBoxRelativeToLayer(block.id, element, layer);
  }
  return imageContentBoxRelativeToLayer(block.id, element, layer);
}

function isParagraphBodyText(block: CanvasSectionBlock) {
  const style = asRecord(block.style);
  const content = asRecord(block.content);
  const presetKey = String(content.presetKey ?? '').toLowerCase();
  const textRole = String(style.textRole ?? '').toLowerCase();
  const variant = String(content.variant ?? '').toLowerCase();
  if (presetKey === 'body') return true;
  if (textRole === 'body' || textRole === 'paragraph') return true;
  if (variant === 'body' || variant.includes('paragraph')) return true;
  if (/\bbody\b/i.test(block.id) && !/eyebrow/i.test(block.id)) return true;
  return false;
}

export function resolveEditorBoundsMode(block: CanvasSectionBlock): EditorBoundsMode {
  if (block.type === 'button') return 'content';
  if (block.type === 'image' || block.type === 'decorative-asset') return 'content';
  if (block.type === 'text') return isParagraphBodyText(block) ? 'text-paragraph' : 'content';
  return 'frame';
}

/** Box source label for alignment/gap guide math (always the visual box). */
export function resolveAlignmentBoxSource(
  block: CanvasSectionBlock,
  bounds?: EditorBoundsBox | null
): 'content' | 'frame' | 'layout' {
  const mode = bounds?.mode ?? resolveEditorBoundsMode(block);
  if (mode === 'content' || mode === 'text-paragraph') return 'content';
  if (mode === 'frame') return 'frame';
  return 'layout';
}

function composeEditorBoundsBox(
  block: CanvasSectionBlock,
  layerWidth: number,
  layerHeight: number,
  frameBox: AlignmentBox,
  contentBox: AlignmentBox | null
): EditorBoundsBox {
  const mode = resolveEditorBoundsMode(block);
  let activeVisualBox = frameBox;
  let editingFrameBox = frameBox;

  if (block.type === 'text' && contentBox) {
    activeVisualBox = contentBox;
    if (mode === 'text-paragraph') {
      editingFrameBox = makeBox(
        block.id,
        frameBox.left,
        frameBox.top,
        frameBox.width,
        Math.max(1, contentBox.bottom - frameBox.top)
      );
    } else {
      editingFrameBox = contentBox;
    }
  } else if (mode === 'content' && contentBox) {
    activeVisualBox = contentBox;
    editingFrameBox = contentBox;
  }

  return {
    blockId: block.id,
    mode,
    layerWidth,
    layerHeight,
    frameBox,
    contentBox,
    activeVisualBox,
    editingFrameBox,
    activeBox: activeVisualBox,
  };
}

function resolveLayoutFrameBoxFromLayer(
  block: CanvasSectionBlock,
  layer: HTMLElement,
  layoutWidth: number,
  layoutHeight: number
): AlignmentBox | null {
  if (!isSectionBackdropModelViewerBlock(block)) return null;
  if (layoutWidth <= 0 || layoutHeight <= 0) return null;
  const colWidth = layoutWidth / TOTAL_COLUMNS;
  const projectionRowsRaw = Number(layer.getAttribute('data-builder-projection-rows'));
  const projectionRows =
    Number.isFinite(projectionRowsRaw) && projectionRowsRaw > 0
      ? projectionRowsRaw
      : Math.max(block.layout.y + block.layout.h - 1, block.layout.h, 1);
  const rowHeight = layoutHeight / projectionRows;
  return createAlignmentBoxFromLayout(block.layout, colWidth, rowHeight, { id: block.id });
}

export function resolveLiveFrameBoxFromLayer(layer: HTMLElement, blockId: string): AlignmentBox | null {
  const root = findBlockRoot(layer, blockId);
  if (!root) return null;
  const box = elementBoxRelativeToLayer(blockId, root, layer);
  if (box.width <= 0 || box.height <= 0) return null;
  return box;
}

/** Live box for alignment/gap guides — uses content bounds when the block mode is content. */
export function resolveLiveActiveBoxFromLayer(
  layer: HTMLElement,
  blockId: string,
  block?: CanvasSectionBlock
): AlignmentBox | null {
  const root = findBlockRoot(layer, blockId);
  if (!root) return null;
  const { layoutWidth, layoutHeight } = resolveLayerLayoutMetrics(layer);
  const frameBox = elementBoxRelativeToLayer(blockId, root, layer);
  if (frameBox.width <= 0 || frameBox.height <= 0) {
    if (block) {
      const layoutBox = resolveLayoutFrameBoxFromLayer(block, layer, layoutWidth, layoutHeight);
      if (layoutBox) return layoutBox;
    }
    return null;
  }

  const contentElement = root.querySelector<HTMLElement>(CONTENT_SELECTOR);
  if (!contentElement) return frameBox;

  const contentRect = contentElement.getBoundingClientRect();
  if (contentRect.width <= 0 || contentRect.height <= 0) return frameBox;

  if (block) {
    const contentBox = contentBoxRelativeToLayer(block, contentElement, layer);
    if (block.type === 'text' && contentBox) return contentBox;
    const mode = resolveEditorBoundsMode(block);
    if (mode === 'content' && contentBox) return contentBox;
    return frameBox;
  }

  return elementBoxRelativeToLayer(blockId, contentElement, layer);
}

export function findBlockRoot(layer: HTMLElement, blockId: string): HTMLElement | null {
  const roots = layer.querySelectorAll<HTMLElement>(BLOCK_ROOT_SELECTOR);
  for (const root of Array.from(roots)) {
    if (root.getAttribute('data-builder-block-id') === blockId) return root;
  }
  return null;
}

export function resolveEditorBoundsFromDom(
  block: CanvasSectionBlock,
  layer: HTMLElement,
  options?: {
    colWidth?: number;
    rowHeight?: number;
  }
): EditorBoundsBox | null {
  const { layoutWidth, layoutHeight } = resolveLayerLayoutMetrics(layer);
  if (
    isSectionBackdropModelViewerBlock(block) &&
    options?.colWidth &&
    options?.rowHeight
  ) {
    const layoutFrameBox = createAlignmentBoxFromLayout(
      block.layout,
      options.colWidth,
      options.rowHeight,
      { id: block.id }
    );
    return composeEditorBoundsBox(block, layoutWidth, layoutHeight, layoutFrameBox, null);
  }
  const root = findBlockRoot(layer, block.id);
  const frameBox =
    root && root.getBoundingClientRect().width > 0 && root.getBoundingClientRect().height > 0
      ? elementBoxRelativeToLayer(block.id, root, layer)
      : options?.colWidth && options?.rowHeight
        ? createAlignmentBoxFromLayout(block.layout, options.colWidth, options.rowHeight, { id: block.id })
        : null;

  if (!frameBox) return null;

  const contentElement = root?.querySelector<HTMLElement>(CONTENT_SELECTOR) ?? null;
  const contentBox = contentElement
    ? contentBoxRelativeToLayer(block, contentElement, layer)
    : null;

  return composeEditorBoundsBox(block, layoutWidth, layoutHeight, frameBox, contentBox);
}

function isFiniteGapAlignmentBox(box: AlignmentBox) {
  return [box.left, box.top, box.right, box.bottom, box.width, box.height].every(Number.isFinite);
}

/** Content/active visual box for gap guide math — always layer-scoped, never document-wide. */
export function resolveGapAlignmentBoxFromLayer(
  block: CanvasSectionBlock,
  layer: HTMLElement
): AlignmentBox | null {
  const bounds = resolveEditorBoundsFromDom(block, layer);
  if (bounds) {
    const visualBox =
      (bounds.mode === 'content' || bounds.mode === 'text-paragraph') && bounds.contentBox
        ? bounds.contentBox
        : bounds.activeBox;
    if (visualBox && isFiniteGapAlignmentBox(visualBox)) {
      return { ...visualBox, id: block.id };
    }
  }

  const layerBox = resolveLiveActiveBoxFromLayer(layer, block.id, block);
  if (layerBox && isFiniteGapAlignmentBox(layerBox)) {
    return { ...layerBox, id: block.id };
  }
  return null;
}

export function resolveEditorBoundsFromDocument(
  block: CanvasSectionBlock,
  options?: {
    layer?: HTMLElement | null;
    colWidth?: number;
    rowHeight?: number;
  }
): EditorBoundsBox | null {
  if (options?.layer) {
    return resolveEditorBoundsFromDom(block, options.layer, {
      colWidth: options.colWidth,
      rowHeight: options.rowHeight,
    });
  }
  if (typeof document === 'undefined') return null;

  const roots = document.querySelectorAll<HTMLElement>(BLOCK_ROOT_SELECTOR);
  const matches: Array<{ layer: HTMLElement; area: number }> = [];
  for (const root of Array.from(roots)) {
    if (root.getAttribute('data-builder-block-id') !== block.id) continue;
    const layer = root.closest<HTMLElement>('[data-builder-editor-layer="true"]');
    if (!layer) continue;
    const rect = layer.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) continue;
    matches.push({ layer, area: rect.width * rect.height });
  }
  if (!matches.length) return null;

  const stageMatch = matches.find((entry) => entry.layer.closest('[data-stage-view="true"]'));
  const bestMatch =
    stageMatch ??
    matches.reduce((best, entry) => (entry.area > best.area ? entry : best), matches[0]);

  return resolveEditorBoundsFromDom(block, bestMatch.layer, {
    colWidth: options?.colWidth,
    rowHeight: options?.rowHeight,
  });
}

function activeBoxMatchesFrame(bounds: EditorBoundsBox) {
  const { frameBox, activeVisualBox } = bounds;
  return (
    Math.abs(frameBox.left - activeVisualBox.left) <= TOLERANCE_PX &&
    Math.abs(frameBox.top - activeVisualBox.top) <= TOLERANCE_PX &&
    Math.abs(frameBox.width - activeVisualBox.width) <= TOLERANCE_PX &&
    Math.abs(frameBox.height - activeVisualBox.height) <= TOLERANCE_PX
  );
}

export function editorBoundsFrameOverlayStyle(bounds: EditorBoundsBox | undefined): React.CSSProperties | undefined {
  if (!bounds) return undefined;
  const { frameBox, activeVisualBox } = bounds;
  if (activeBoxMatchesFrame(bounds)) {
    return undefined;
  }
  return {
    left: activeVisualBox.left - frameBox.left,
    top: activeVisualBox.top - frameBox.top,
    width: activeVisualBox.width,
    height: activeVisualBox.height,
  };
}

export function editorBoundsCanvasOverlayStyle(bounds: EditorBoundsBox | undefined): React.CSSProperties | undefined {
  if (!bounds) return undefined;
  const { activeVisualBox } = bounds;
  return {
    left: activeVisualBox.left,
    top: activeVisualBox.top,
    width: activeVisualBox.width,
    height: activeVisualBox.height,
  };
}

export const editorBoundsOverlayStyle = editorBoundsFrameOverlayStyle;

function boxesEqual(a: EditorBoundsBox | undefined, b: EditorBoundsBox | undefined) {
  if (!a || !b) return a === b;
  const keys: Array<keyof AlignmentBox> = ['left', 'top', 'width', 'height', 'right', 'bottom', 'centerX', 'centerY'];
  return (
    a.mode === b.mode &&
    Math.abs(a.layerWidth - b.layerWidth) <= TOLERANCE_PX &&
    Math.abs(a.layerHeight - b.layerHeight) <= TOLERANCE_PX &&
    keys.every((key) => Math.abs(Number(a.frameBox[key]) - Number(b.frameBox[key])) <= TOLERANCE_PX) &&
    keys.every((key) => Math.abs(Number(a.activeVisualBox[key]) - Number(b.activeVisualBox[key])) <= TOLERANCE_PX) &&
    keys.every((key) => Math.abs(Number(a.editingFrameBox[key]) - Number(b.editingFrameBox[key])) <= TOLERANCE_PX) &&
    keys.every((key) => Math.abs(Number(a.activeBox[key]) - Number(b.activeBox[key])) <= TOLERANCE_PX) &&
    Boolean(a.contentBox) === Boolean(b.contentBox) &&
    (!a.contentBox ||
      !b.contentBox ||
      keys.every((key) => Math.abs(Number(a.contentBox?.[key]) - Number(b.contentBox?.[key])) <= TOLERANCE_PX))
  );
}

function boundsMapsEqual(
  a: Record<string, EditorBoundsBox>,
  b: Record<string, EditorBoundsBox>
) {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (!boxesEqual(a[key], b[key])) return false;
  }
  return true;
}

export function useEditorBoundsRegistry({
  enabled,
  layerRef,
  blocks,
  colWidth,
  rowHeight,
}: UseEditorBoundsRegistryOptions) {
  const [boundsByBlockId, setBoundsByBlockId] = React.useState<Record<string, EditorBoundsBox>>({});
  const rafRef = React.useRef<number | null>(null);
  const boundsRef = React.useRef<Record<string, EditorBoundsBox>>({});

  const measure = React.useCallback(() => {
    const layer = layerRef.current;
    if (!enabled || !layer || colWidth <= 0 || rowHeight <= 0) {
      if (Object.keys(boundsRef.current).length > 0) {
        boundsRef.current = {};
        setBoundsByBlockId({});
      }
      return;
    }

    const next: Record<string, EditorBoundsBox> = {};
    blocks.forEach((block) => {
      const bounds = resolveEditorBoundsFromDom(block, layer, { colWidth, rowHeight });
      if (bounds) next[block.id] = bounds;
    });

    if (!boundsMapsEqual(boundsRef.current, next)) {
      boundsRef.current = next;
      setBoundsByBlockId(next);
    }
  }, [blocks, colWidth, enabled, layerRef, rowHeight]);

  const scheduleMeasure = React.useCallback(() => {
    if (typeof window === 'undefined') return;
    if (rafRef.current != null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      measure();
    });
  }, [measure]);

  React.useLayoutEffect(() => {
    scheduleMeasure();
  }, [scheduleMeasure]);

  React.useEffect(() => {
    const layer = layerRef.current;
    if (!enabled || !layer || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(scheduleMeasure);
    observer.observe(layer);
    layer.querySelectorAll<HTMLElement>(`${BLOCK_ROOT_SELECTOR}, ${CONTENT_SELECTOR}`).forEach((node) => {
      observer.observe(node);
    });
    scheduleMeasure();
    return () => observer.disconnect();
  }, [blocks, enabled, layerRef, scheduleMeasure]);

  React.useEffect(
    () => () => {
      if (rafRef.current != null && typeof window !== 'undefined') {
        window.cancelAnimationFrame(rafRef.current);
      }
    },
    []
  );

  return boundsByBlockId;
}
